/**
 * Mobil uygulama bağlantı karekodu.
 *
 * Octo.Mobile ilk açılışta Octo.Dal adresini istiyor; adresi telefonda elle yazmak
 * ("https://xdal.reniumglobal.com") hem yorucu hem hataya açık. Karekodun içeriği düz
 * API adresidir: uygulama okutunca doğrulayıp giriş ekranına geçer, herhangi bir kamera
 * uygulaması da okuyabilir.
 *
 * Blazor karşılığı `Octo.Web.Core/Components/MobileAppQrSheet.razor`; orada karekod
 * sunucuda (QRCoder) üretiliyor. Burada tarayıcıda üretiliyor ama dış CDN yok: `qrcode`
 * paketi uygulamayla birlikte geliyor ve yalnız bu pencere açıldığında yükleniyor —
 * intranet kurulumlarındaki "dışarıya çıkmasın" koşulu korunuyor.
 */
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Check, Copy, QrCode, Smartphone } from 'lucide-react';
import { Alert } from '@/shared/ui/Alert';
import { Button } from '@/shared/ui/Button';
import { Dialog } from '@/shared/ui/Dialog';
import { Spinner } from '@/shared/ui/Spinner';
import { runtimeConfig } from '@/app/config/runtimeConfig';

export function MobileAppQrDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { t } = useTranslation();
  const serverUrl = (runtimeConfig().dalUrl ?? '').replace(/\/+$/, '');
  const [dataUri, setDataUri] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!open || !serverUrl) return;
    let cancelled = false;
    void (async () => {
      try {
        const QRCode = await import('qrcode');
        const uri = await QRCode.toDataURL(serverUrl, {
          errorCorrectionLevel: 'M',
          margin: 1,
          width: 320,
          // Karekod HER ZAMAN siyah-beyaz: koyu temada tema renkleriyle çizmek
          // okuyucuların kontrast beklentisini bozuyor.
          color: { dark: '#000000', light: '#ffffff' },
        });
        if (!cancelled) setDataUri(uri);
      } catch {
        if (!cancelled) setFailed(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, serverUrl]);

  const copy = () => {
    void (async () => {
      try {
        await navigator.clipboard.writeText(serverUrl);
        setCopied(true);
        window.setTimeout(() => setCopied(false), 1500);
      } catch {
        // Pano erişimi yoksa (HTTP, izin) adres zaten ekranda yazılı.
      }
    })();
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        if (!o) onClose();
      }}
      title={t('shell.user.mobileQr')}
      size="sm"
      footer={
        <Button variant="secondary" onClick={onClose}>
          {t('common:Close')}
        </Button>
      }
    >
      <div className="flex flex-col items-center gap-3">
        {!serverUrl || failed ? (
          <Alert tone="warning">{t('shell.user.mobileQrUnavailable')}</Alert>
        ) : dataUri ? (
          // Beyaz zemin koyu temada da korunur: karekodun okunabilirliği temaya bağlı olamaz.
          <div className="rounded-lg bg-white p-3 shadow-[var(--shadow)]">
            <img src={dataUri} alt={t('shell.user.mobileQr')} className="h-56 w-56" />
          </div>
        ) : (
          <div className="flex h-56 w-56 items-center justify-center">
            <Spinner />
          </div>
        )}

        <p className="text-fg-muted flex items-center gap-2 text-center text-sm">
          <Smartphone className="h-4 w-4 shrink-0" />
          {t('shell.user.mobileQrHint')}
        </p>

        {serverUrl ? (
          <div className="border-border bg-bg-muted flex w-full items-center gap-2 rounded-md border px-2 py-1.5">
            <QrCode className="text-fg-subtle h-4 w-4 shrink-0" />
            <span className="min-w-0 flex-1 truncate font-mono text-xs" title={serverUrl}>
              {serverUrl}
            </span>
            <Button variant="ghost" size="sm" onClick={copy}>
              {copied ? <Check className="text-success h-4 w-4" /> : <Copy className="h-4 w-4" />}
              {copied ? t('common:Copied') : t('common:Copy')}
            </Button>
          </div>
        ) : null}
      </div>
    </Dialog>
  );
}
